import {
  SHIFT_STATUS,
  isActiveShift,
  isClosedShift,
  isBillableShift,
  isPayableShift,
} from "../constants";

import {
  calculateHours,
  hasEnded,
  isToday,
  isWithinHoursBefore,
  rangesOverlap,
} from "./timeUtils";

function getShiftStart(shift) {
  return shift?.start || shift?.startTime || null;
}

function getShiftEnd(shift) {
  return shift?.end || shift?.endTime || null;
}

function toMillis(value) {
  if (value == null) {
    return NaN;
  }

  try {
    if (typeof value?.toMillis === "function") {
      return value.toMillis();
    }

    if (typeof value?.toDate === "function") {
      return value.toDate().getTime();
    }

    if (value instanceof Date) {
      return value.getTime();
    }

    if (typeof value === "number") {
      return value;
    }

    return new Date(value).getTime();
  } catch {
    return NaN;
  }
}

export function normalizeShiftStatus(status) {
  return String(status || "").toLowerCase();
}

export function getShiftStatus(shift) {
  return normalizeShiftStatus(shift?.status || SHIFT_STATUS.OPEN);
}

export function getShiftRole(shift) {
  return String(
    shift?.role ||
      shift?.workerRole ||
      shift?.requiredPosition ||
      shift?.position ||
      ""
  ).toUpperCase();
}

export function getShiftFacilityId(shift) {
  return String(shift?.facilityId || "").toLowerCase();
}

export function getShiftWorkerId(shift) {
  return String(
    shift?.workerId ||
      shift?.assignedWorkerId ||
      shift?.assignedWorkerUid ||
      shift?.assignedWorkerUids?.[0] ||
      ""
  );
}

export function getShiftHours(shift) {
  return calculateHours(getShiftStart(shift), getShiftEnd(shift));
}

export function isShiftActive(shift) {
  return isActiveShift(getShiftStatus(shift));
}

export function isShiftClosed(shift) {
  return isClosedShift(getShiftStatus(shift));
}

export function isShiftBillable(shift) {
  return isBillableShift(getShiftStatus(shift));
}

export function isShiftPayable(shift) {
  return isPayableShift(getShiftStatus(shift));
}

export function isShiftCompleted(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.COMPLETED;
}

export function isShiftOpen(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.OPEN;
}

export function isShiftOffered(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.OFFERED;
}

export function isShiftRequested(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.REQUESTED;
}

export function isShiftAssigned(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.ASSIGNED;
}

export function isShiftActivated(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.ACTIVATED;
}

export function isShiftArrived(shift) {
  return getShiftStatus(shift) === SHIFT_STATUS.ARRIVED;
}

export function shouldShowInToday(shift) {
  return isToday(getShiftStart(shift)) && !isShiftClosed(shift);
}

export function shouldShowInUpcoming(shift) {
  return (
    !hasEnded(getShiftEnd(shift)) &&
    !isToday(getShiftStart(shift)) &&
    !isShiftClosed(shift)
  );
}

export function shouldShowInHistory(shift) {
  return hasEnded(getShiftEnd(shift)) || isShiftClosed(shift);
}

export function canActivateShift(shift, activationWindowHours = 2) {
  return (
    isShiftAssigned(shift) &&
    isWithinHoursBefore(getShiftStart(shift), activationWindowHours)
  );
}

export function canArriveShift(shift) {
  return isShiftActivated(shift);
}

export function canCompleteShift(shift, nowMs = Date.now()) {
  const endMs = toMillis(
    getShiftEnd(shift)
  );

  return (
    (isShiftArrived(shift) || isShiftActivated(shift)) &&
    Number.isFinite(endMs) &&
    nowMs >= endMs
  );
}

export function shiftOverlapsShift(shiftA, shiftB) {
  return rangesOverlap(
    getShiftStart(shiftA),
    getShiftEnd(shiftA),
    getShiftStart(shiftB),
    getShiftEnd(shiftB)
  );
}

export function workerHasOverlap(targetShift, workerShifts = []) {
  const targetId = targetShift?.id || targetShift?.shiftId;

  return workerShifts.some((shift) => {
    const shiftId = shift?.id || shift?.shiftId;

    if (targetId && shiftId && targetId === shiftId) return false;
    if (isShiftClosed(shift)) return false;

    return shiftOverlapsShift(targetShift, shift);
  });
}

export function canWorkerRequestShift(targetShift, workerShifts = []) {
  if (!isShiftOpen(targetShift)) return false;
  return !workerHasOverlap(targetShift, workerShifts);
}

export function canWorkerAcceptOffer(targetShift, workerShifts = []) {
  if (!isShiftOffered(targetShift)) return false;
  return !workerHasOverlap(targetShift, workerShifts);
}

export function canPlannerAssignShift(targetShift, workerShifts = []) {
  if (isShiftClosed(targetShift)) return false;
  return !workerHasOverlap(targetShift, workerShifts);
}